import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Modal, Timeline, Card, Button, Space, Tag, Typography, Descriptions, message, Popconfirm, Divider, Row, Col } from 'antd';
import {
  ClockCircleOutlined,
  RollbackOutlined,
  EyeOutlined,
  SwapOutlined,
  CheckCircleOutlined,
  HistoryOutlined,
  UserOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import CodeEditor from 'components/shared/CodeEditor';
import templateService from 'services/templateService';

const { Text, Title } = Typography;

/**
 * VersionHistory - Aura Audit Log
 * Revamped for "Aura" aesthetic
 */
const VersionHistory = ({ visible, onClose, template, onRollback }) => {
  const [loading, setLoading] = useState(false);
  const [versions, setVersions] = useState([]);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [compareModalVisible, setCompareModalVisible] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [compareVersions, setCompareVersions] = useState({ v1: null, v2: null });

  useEffect(() => {
    if (visible && template?.templateId) {
      loadVersionHistory();
      setCurrentVersion(template.version || 1);
    }
  }, [visible, template]);

  const loadVersionHistory = async () => {
    if (!template?.templateId) return;
    setLoading(true);
    try {
      const response = await templateService.getVersionHistory(template.templateId);
      if (response.success) setVersions(response.data || []);
    } catch (error) {
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleViewVersion = (version) => {
    setSelectedVersion(version);
    setViewModalVisible(true);
  };

  const handleCompareVersions = (v1, v2) => {
    setCompareVersions({ v1, v2 });
    setCompareModalVisible(true);
  };

  const handleRollback = async (version) => {
    try {
      setLoading(true);
      await templateService.rollbackToVersion(template.templateId, version.version);
      message.success(`INTELLIGENCE RESTORED TO V${version.version}`);
      onRollback?.();
      onClose();
    } catch (error) {
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <AuraModal
        title={
          <ModalTitle>
            <HistoryOutlined style={{ color: 'var(--aura-blue)' }} />
            <Text strong style={{ fontSize: 13, letterSpacing: 1 }}>AUDIT LOG: {template?.templateName}</Text>
          </ModalTitle>
        }
        open={visible}
        onCancel={onClose}
        width={900}
        footer={[
          <CloseAction key="close" onClick={onClose}>CLOSE LOG</CloseAction>,
        ]}
        className="aura-modal-glass"
      >
        <HistoryContainer>
           <Timeline mode="left">
             {versions.map((v) => (
               <Timeline.Item 
                 key={v.versionId} 
                 color={v.version === currentVersion ? 'var(--aura-blue)' : '#cbd5e1'}
                 dot={v.version === currentVersion && <ClockCircleOutlined style={{ fontSize: 16 }} />}
               >
                 <AuditCard $active={v.version === currentVersion}>
                   <AuditHeader>
                     <Space size="middle">
                       <VersionTag>{v.version === currentVersion ? 'CURRENT' : `V${v.version}`}</VersionTag>
                       <Text strong style={{ fontSize: 12, color: 'var(--aura-navy)' }}>
                         {v.changeDescription || 'Protocol Optimization'}
                       </Text>
                     </Space>
                     <Text style={{ fontSize: 11, color: 'var(--aura-slate)' }}>
                       {v.changedDate ? new Date(v.changedDate).toLocaleString() : '---'}
                     </Text>
                   </AuditHeader>
                   
                   <AuditMeta>
                      <MetaItem><UserOutlined /> {v.changedBy || 'SYSTEM'}</MetaItem>
                      <MetaItem><FileTextOutlined /> {v.changesCount || 0} MODS</MetaItem>
                   </AuditMeta>

                   <AuditActions>
                     <Space>
                       <ActionBtn size="small" icon={<EyeOutlined />} onClick={() => handleViewVersion(v)}>VIEW</ActionBtn>
                       <ActionBtn 
                         size="small" 
                         icon={<SwapOutlined />} 
                         disabled={v.version === currentVersion}
                         onClick={() => handleCompareVersions(versions.find(x => x.version === currentVersion), v)}
                       >
                         COMPARE
                       </ActionBtn>
                       {v.version !== currentVersion && (
                         <Popconfirm
                           title="RESTORE PROTOCOL?"
                           description={`Rollback logic to Version ${v.version}. Current changes will be archived.`}
                           onConfirm={() => handleRollback(v)}
                           okText="RESTORE"
                           cancelText="ABORT"
                         >
                           <RestoreBtn size="small" icon={<RollbackOutlined />}>RESTORE</RestoreBtn>
                         </Popconfirm>
                       )}
                     </Space>
                   </AuditActions>
                 </AuditCard>
               </Timeline.Item>
             ))}
           </Timeline>
        </HistoryContainer>
      </AuraModal>

      {/* View Version Modal */}
      <AuraModal
        title={<><Text strong style={{ fontSize: 12 }}>ARCHIVE SOURCE: V{selectedVersion?.version}</Text></>}
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        width={1100}
        footer={[<CloseAction key="c" onClick={() => setViewModalVisible(false)}>CLOSE</CloseAction>]}
      >
        {selectedVersion && (
          <SourceGrid gutter={24}>
            <Col span={12}>
               <SectionHeader>PROMPT DEFINITION</SectionHeader>
               <CodeBox>
                  <CodeEditor
                    language="json"
                    value={JSON.stringify(selectedVersion.promptDefinition || {}, null, 2)}
                    readOnly
                    height="450px"
                  />
               </CodeBox>
            </Col>
            <Col span={12}>
               <SectionHeader>HTML FRAME</SectionHeader>
               <CodeBox>
                  <CodeEditor
                    language="html"
                    value={selectedVersion.htmlTemplate || ''}
                    readOnly
                    height="450px"
                  />
               </CodeBox>
            </Col>
          </SourceGrid>
        )}
      </AuraModal>

      {/* Compare Modal */}
      <AuraModal
        title={<><SwapOutlined /> <Text strong style={{ fontSize: 12 }}>DIFF ANALYSIS: V{compareVersions.v1?.version} vs V{compareVersions.v2?.version}</Text></>}
        open={compareModalVisible}
        onCancel={() => setCompareModalVisible(false)}
        width={1400}
        footer={[<CloseAction key="c" onClick={() => setCompareModalVisible(false)}>CLOSE ANALYSIS</CloseAction>]}
      >
         <CompareLayout>
            <ComparePane>
               <SectionHeader>VERSION {compareVersions.v1?.version} (ANCHOR)</SectionHeader>
                <CodeBox>
                  <CodeEditor
                    language="json"
                    value={JSON.stringify(compareVersions.v1?.promptDefinition || {}, null, 2)}
                    readOnly
                    height="500px"
                  />
                </CodeBox>
            </ComparePane>
            <VerticalDivider />
            <ComparePane>
               <SectionHeader>VERSION {compareVersions.v2?.version} (TARGET)</SectionHeader>
                <CodeBox>
                  <CodeEditor
                    language="json"
                    value={JSON.stringify(compareVersions.v2?.promptDefinition || {}, null, 2)}
                    readOnly
                    height="500px"
                  />
                </CodeBox>
            </ComparePane>
         </CompareLayout>
      </AuraModal>
    </>
  );
};

const AuraModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 24px;
    padding: 24px;
    overflow: hidden;
  }
  .ant-modal-header {
    border-bottom: 1px solid #f1f5f9;
    padding-bottom: 16px;
    margin-bottom: 24px;
  }
`;

const ModalTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HistoryContainer = styled.div`
  padding: 12px;
  max-height: 550px;
  overflow-y: auto;
  
  .ant-timeline-item-tail { border-left: 2px dashed #e2e8f0; }
`;

const AuditCard = styled.div`
  background: ${props => props.$active ? '#f8fafc' : 'white'};
  border: 1px solid ${props => props.$active ? 'var(--aura-blue)' : '#f1f5f9'};
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.02);
  transition: all 0.3s;
  
  &:hover {
    transform: translateX(8px);
    box-shadow: 0 8px 24px rgba(0,0,0,0.04);
  }
`;

const AuditHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const VersionTag = styled.span`
  background: var(--aura-navy);
  color: white;
  font-size: 9px;
  font-weight: 900;
  padding: 4px 10px;
  border-radius: 6px;
  letter-spacing: 0.5px;
`;

const AuditMeta = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 16px;
`;

const MetaItem = styled.span`
  font-size: 10px;
  font-weight: 800;
  color: var(--aura-slate);
  display: flex;
  align-items: center;
  gap: 6px;
`;

const AuditActions = styled.div`
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid #f1f5f9;
  padding-top: 16px;
`;

const ActionBtn = styled(Button)`
  font-weight: 850 !important;
  font-size: 10px !important;
  letter-spacing: 0.5px !important;
  border-radius: 8px !important;
  border-color: #f1f5f9 !important;
  color: var(--aura-slate) !important;
`;

const RestoreBtn = styled(Button)`
  background: var(--aura-navy) !important;
  color: white !important;
  font-weight: 900 !important;
  font-size: 10px !important;
  border: none !important;
  border-radius: 8px !important;
`;

const CloseAction = styled(Button)`
  height: 44px !important;
  padding: 0 32px !important;
  border-radius: 12px !important;
  font-weight: 900 !important;
  font-size: 11px !important;
  letter-spacing: 1px !important;
  border: 1px solid #f1f5f9 !important;
`;

const SourceGrid = styled(Row)`
  margin-top: 12px;
`;

const SectionHeader = styled.div`
  font-size: 10px;
  font-weight: 900;
  color: var(--aura-slate);
  letter-spacing: 1px;
  margin-bottom: 12px;
  padding-left: 4px;
`;

const CodeBox = styled.div`
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid #f1f5f9;
`;

const CompareLayout = styled.div`
  display: flex;
  gap: 24px;
`;

const ComparePane = styled.div`
  flex: 1;
`;

const VerticalDivider = styled.div`
  width: 1px;
  background: #f1f5f9;
`;

export default VersionHistory;
